/**
 * TodayDivider — the horizontal "Today" line for date-ordered lists.
 *
 * Same look as the chart Today line (src/lib/todayLine.ts): thin brand green
 * with a small "Today" cap. With `scrollIntoViewOnMount`, the nearest
 * scrollable container (a drawer, a panel) starts scrolled to it — the page
 * itself is never scrolled.
 */
import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { TODAY_LINE_COLOR, scrollToToday } from '@/lib/todayLine';

interface TodayDividerProps {
  scrollIntoViewOnMount?: boolean;
  className?: string;
}

export function TodayDivider({ scrollIntoViewOnMount = false, className }: TodayDividerProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollIntoViewOnMount && ref.current) scrollToToday(ref.current);
  }, [scrollIntoViewOnMount]);

  return (
    <div
      ref={ref}
      role="separator"
      aria-label="Today"
      className={cn('flex items-center gap-2 py-2', className)}
    >
      <span
        className="rounded-sm px-1 text-[10px] font-semibold leading-4 text-black"
        style={{ backgroundColor: TODAY_LINE_COLOR }}
      >
        Today
      </span>
      <span className="h-px flex-1" style={{ backgroundColor: TODAY_LINE_COLOR }} />
    </div>
  );
}
